import { reportSchemaFailure } from "_shared/_errors/client-error-reporter";
import { SCHEMA_FAILURE_ERROR } from "_shared/_errors/schema-failure-error";
import z from "zod";

/**
 * Why an id named in an explicit get-by-ids request is not among the resources returned.
 *
 * - `missing` — the id names nothing: never created, or deleted. The bulk form of 404.
 * - `unauthorized` — the resource exists and the viewer may not have it. The bulk form of 403.
 *
 * A filter, search or ordered list leaves out what the viewer may not see without saying so. A
 * get-by-ids request named each id, so the API answers each one (`AttorneyHub.Queries.ByIdsResult`).
 */
export const UNAVAILABLE_REASONS = ["missing", "unauthorized"] as const;
export type UnavailableReason = (typeof UNAVAILABLE_REASONS)[number];

export const UnavailableIdJSONZ = z.object({
  id: z.string(),
  reason: z.enum(UNAVAILABLE_REASONS),
});
export type UnavailableIdJSON = z.infer<typeof UnavailableIdJSONZ>;

/** The wire shape of `ByIdsResult<T>`, for an endpoint typed by its item rather than its schema. */
export type ByIdsResultJSON<T> = { found: T[]; unavailable: UnavailableIdJSON[] };

/** The wire shape of `ByIdsResult<T>`: what the viewer may have, and why every other id is not there. */
export function byIdsResultJSONZ<T extends z.ZodType>(item: T) {
  return z.object({
    found: z.array(item),
    unavailable: z.array(UnavailableIdJSONZ),
  });
}

/**
 * Validates a by-ids response inside a `queryFn`, where RTK Query's own schema handling does not
 * reach. A mismatch is reported the way `logSchemaFailure` reports one, and answered with
 * {@link SCHEMA_FAILURE_ERROR}, so the caller sees the same error as from a `responseSchema`.
 */
export function parseByIdsResult<T extends z.ZodType>(
  schema: ReturnType<typeof byIdsResultJSONZ<T>>,
  raw: unknown,
  report: { schemaName: string; operation: string },
): { data: z.infer<ReturnType<typeof byIdsResultJSONZ<T>>> } | { error: typeof SCHEMA_FAILURE_ERROR } {
  const parsed = schema.safeParse(raw);
  if (parsed.success) return { data: parsed.data };
  reportSchemaFailure({
    schemaName: report.schemaName,
    operation: report.operation,
    phase: "response",
    fields: parsed.error.issues.map((issue) => issue.path.map(String).join(".")),
  });
  return { error: SCHEMA_FAILURE_ERROR };
}
